import { ProductionPiece } from 'app/models/ProductionPiece';
import { extractColorReference } from 'app/models/Color';

const PANEL_TYPES = new Set(['door', 'pane', 'plate']);

// Sheet zones, from top to bottom.
enum Zone {
  Head,
  Walk,
  ColorBlocks,
  Tail,
}

interface Placement {
  zone: Zone;
  position: number;
}

const getRank = (piece: ProductionPiece): number | undefined =>
  typeof piece.pickingRank === 'number' ? piece.pickingRank : undefined;

const getPanelColor = (piece: ProductionPiece): string | undefined =>
  PANEL_TYPES.has(piece.typeName)
    ? extractColorReference(piece.reference)
    : undefined;

// Rank of the closest ranked piece above, preferring one of the same type.
const getAnchorRank = (pieces: ProductionPiece[], index: number): number => {
  const above = pieces.slice(0, index).reverse();
  const sameType = above.find(
    piece =>
      getRank(piece) !== undefined && piece.typeName === pieces[index].typeName,
  );
  const anchor = sameType ?? above.find(piece => getRank(piece) !== undefined);
  return getRank(anchor!)!;
};

/**
 * Reorders legacy-sorted pieces along the factory walk (`pickingRank`).
 * Pieces without rank keep a place derived from the legacy order:
 * - colored panels: one block per color after the walk (zones without address);
 * - pieces above the first / below the last ranked piece: stay at head / tail;
 * - other pieces: right after the closest ranked piece of the same type above them.
 * Without any ranked piece, the legacy order is returned unchanged.
 */
export const orderByLocation = (
  pieces: ProductionPiece[],
): ProductionPiece[] => {
  const rankedIndexes = pieces
    .map((piece, index) => (getRank(piece) === undefined ? -1 : index))
    .filter(index => index >= 0);

  if (rankedIndexes.length === 0) return pieces;

  const firstRanked = rankedIndexes[0];
  const lastRanked = rankedIndexes[rankedIndexes.length - 1];
  const colorBlocks = new Map<string, number>();

  const getPlacement = (piece: ProductionPiece, index: number): Placement => {
    const rank = getRank(piece);
    if (rank !== undefined) return { zone: Zone.Walk, position: rank };

    const color = getPanelColor(piece);
    if (color) {
      if (!colorBlocks.has(color)) colorBlocks.set(color, colorBlocks.size);
      return { zone: Zone.ColorBlocks, position: colorBlocks.get(color)! };
    }

    if (index < firstRanked) return { zone: Zone.Head, position: 0 };
    if (index > lastRanked) return { zone: Zone.Tail, position: 0 };

    return { zone: Zone.Walk, position: getAnchorRank(pieces, index) + 0.5 };
  };

  return pieces
    .map((piece, index) => ({ piece, index, ...getPlacement(piece, index) }))
    .sort(
      (a, b) => a.zone - b.zone || a.position - b.position || a.index - b.index,
    )
    .map(({ piece }) => piece);
};
